import React, { useEffect, useState } from "react";
import useFirebaseUserData from "../hooks/useFirebaseUserData";
import { Button } from "./ui/button";
import { Check } from "lucide-react";
import useSegmentActions from "../hooks/useSegmentActions";
import useFiringActions from "../hooks/useFiringActions";
import { getFileListing, deleteFile } from "../helpers/storageHelpers";

const DatabaseIntegrity = () => {
  const findDuffItemsInUser = (user: any, data: any) => {
    const duff_items = [];
    if (data.firings) {
      for (const [id, firing] of Object.entries(data.firings) as [string, any][]) {
        if (data.projects && !data.projects[firing.project_id]) {
          duff_items.push({
            message: `Missing Project ID for: ${user}/firings/${id} - Project ID is: ${firing.project_id}`,
            object_type: "FIRING",
            object_reference: id,
          });
        }
      }
    }
    if (data.segments) {
      for (const [id, segment] of Object.entries(data.segments) as [string, any][]) {
        if (data.firings && !data.firings[segment.firing_id]) {
          duff_items.push({
            message: `Missing Firing ID for: ${user}/segments/${id}`,
            object_type: "SEGMENT",
            object_reference: id,
          });
        }
      }
    }
    if (data.projects) {
      for (const [id, project] of Object.entries(data.projects) as [string, any][]) {
        if (project.photo) {
          duff_items.push({
            message: `Found old-style photo in: ${user}/projects/${id}`,
            object_type: "PHOTO",
            object_reference: id,
          });
        }
        if (project.photos) {
          project.photos.forEach((photo: any, index: number) => {
            if (photo.photo.substring(0, 4) === "file") {
              duff_items.push({
                message: `Photo has a file:// URL in ${user}/projects/${id}/photos[${index}]`,
                object_type: "PHOTO",
                object_reference: id,
              });
            }
            if (!photo.photo1024) {
              duff_items.push({
                message: `Photo lacks photo1024 field in ${user}/projects/${id}/photos[${index}]`,
                object_type: "PHOTO",
                object_reference: id + `${index}:1024`,
              });
            }
            if (!photo.photo256) {
              duff_items.push({
                message: `Photo lacks photo256 field in ${user}/projects/${id}/photos[${index}]`,
                object_type: "PHOTO",
                object_reference: id + `${index}:256`,
              });
            }
          });
        }
      }
    }

    return { user_id: user, problems: duff_items };
  };

  const URImatch = (userDataUri: any, file: any) => {
    const decodedURI = decodeURIComponent(userDataUri);
    return decodedURI.includes(file);
  };
  const URIWithinUserData = (user_record: any, file: any) => {
    let found = false;
    if (user_record.projects) {
      Object.values(user_record.projects).some((project: any) => {
        if (project.photo && URImatch(project.photo, file)) {
          found = true;
          return true;
        }
        if (project.photos) {
          project.photos.some((photo: any) => {
            if (URImatch(photo.photo, file)) {
              found = true;
              return true;
            }
            if (photo.photo1024 && URImatch(photo.photo1024.uri, file)) {
              found = true;
              return true;
            }
            if (photo.photo256 && URImatch(photo.photo256.uri, file)) {
              found = true;
              return true;
            }
          });
        }
        return false;
      });
    }
    return found;
  };
  const findOrphanedFilesInStorage = (path: any, all_files: any) => {
    const relevantUserData = (userData as any)[path];
    const relevantFiles = [...all_files[path]];
    const stillRelevantFiles = relevantFiles.filter((file) => {
      const found = URIWithinUserData(relevantUserData, file);
      return !found;
    });
    return stillRelevantFiles;
  };
  const findStorageProblemInPrefix = (path: any, all_files: any) => {
    const orphanedFiles = findOrphanedFilesInStorage(path, all_files);
    const new_duff_items = orphanedFiles.map((file) => {
      return {
        message: `Orphaned File found at url: ${file}`,
        object_type: "ORPHANED_FILE",
        object_reference: file,
      };
    });
    return { prefix: path, problems: new_duff_items };
  };
  const userData = useFirebaseUserData();
  const { deleteSegment } = useSegmentActions();
  const { deleteFiring } = useFiringActions();
  const [files, setFiles] = useState({});

  const pullFileListing = async () => {
    const all_files = await getFileListing();
    setFiles(all_files);
  };
  useEffect(() => {
    pullFileListing();
  }, []);

  if (!userData) return <div>Loading...</div>;

  const usersWithDuffItems = Object.keys(userData).map((key) =>
    findDuffItemsInUser(key, userData[key])
  );

  const resolveProblem = ({ object_type, object_reference }: { message: any; object_type: any; object_reference: any }) => {
    switch (object_type) {
      case "SEGMENT":
        deleteSegment(object_reference, false);
        break;
      case "FIRING":
        deleteFiring(object_reference, false);
        break;
      case "ORPHANED_FILE":
        deleteFile(object_reference);
        break;
    }
  };
  const culledUsersWithDuffItems = usersWithDuffItems.filter((item) => item.problems.length !== 0);

  const storageProblems =
    files && Object.keys(files).map((prefix) => findStorageProblemInPrefix(prefix, files));

  const culledStorageProblems =
    storageProblems && storageProblems.filter((item) => item.problems.length !== 0);
  return (
    <div>
      {culledUsersWithDuffItems.length !== 0 ? (
        <div>
          Found Inconsistent Records:
          {Object.keys(usersWithDuffItems).map((key: any) => (
            <ul key={(usersWithDuffItems as any)[key].user_id}>
              {(usersWithDuffItems as any)[key].problems.length !== 0 && (
                <li key={key}>
                  {(usersWithDuffItems as any)[key].user_id}
                  <ul>
                    {(usersWithDuffItems as any)[key].problems.map((problem: any) => (
                      <li key={problem.object_reference}>
                        {problem.message}
                        {problem.object_type !== "PHOTO" && (
                          <Button variant="destructive" size="xs" onClick={() => resolveProblem(problem)}>
                            Delete {problem.object_type}
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
              )}
            </ul>
          ))}
        </div>
      ) : (
        <div>
          <Check className="inline-block h-4 w-4 text-green-600" />
          No JSON state inconsistencies found
        </div>
      )}
      {culledStorageProblems.length !== 0 ? (
        <div>
          Some Storage Problems:
          <ul>
            {culledStorageProblems.map(({ prefix, problems }) => (
              <li key={prefix}>
                {prefix}
                <ul>
                  {problems.map(({ message, object_type, object_reference }) => (
                    <li key={object_reference}>
                      {message}
                      <Button
                        variant="destructive"
                        size="xs"
                        onClick={() =>
                          resolveProblem({
                            message,
                            object_type,
                            object_reference,
                          })
                        }
                      >
                        Delete {object_type}
                      </Button>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div>
          <Check className="inline-block h-4 w-4 text-green-600" />
          No Storage Inconsisentices found
        </div>
      )}
    </div>
  );
};
export default DatabaseIntegrity;
